import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import cx from 'classnames';
import { MODULES } from 'components/Access';
import FEATURES from 'constants/features';
import { ConfigContext } from 'context/config';
import {
  bindMenu,
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { NavItem } from 'modules/messages/components/MessagesNav';
import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  Fragment,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';
import { sessionReset } from 'services/auth.service';
import { getInboxes } from 'store/modules/conversations/actions';
import { selectUnreadCount } from 'store/modules/conversations/selectors';
import WithFeature, { SomeFeatures } from './WithFeature';
import {
  AcademyIcon,
  CategoryIcon,
  CheckCircleIcon,
  ConnectIcon,
  DashboardIcon,
  HelpIcon,
  LiveHelpIcon,
  LogoutIcon,
  MarketIcon,
  MessageDotsIcon,
  MessageIcon,
  MessageReportIcon,
  PhoneIcon,
  SettingsIcon,
  ToolIcon,
  UserCogIcon,
  UserIcon,
  UsersIcon,
} from './icons';

const DRAWER_WIDTH = 220;
const DRAWER_WIDTH_COLLAPSED = 52;

const MessageIconWithUnread = (props) => {
  const unread = useSelector(selectUnreadCount);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getInboxes());
  }, []);

  return (
    <Badge color="error" variant="dot" invisible={!unread}>
      <MessageIcon {...props} />
    </Badge>
  );
};

const DrawerHeader = styled(Box, {
  shouldForwardProp(propName) {
    return propName !== 'open';
  },
})(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.navbar,
  '& .logo': {
    maxHeight: 38,
    width: '100%',
    margin: !open && 'auto',
  },
}));

const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme, width) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme, DRAWER_WIDTH_COLLAPSED),
    '& .MuiDrawer-paper': closedMixin(theme, DRAWER_WIDTH_COLLAPSED),
  }),
  '& .MuiListItemButton-root': {
    borderRadius: 6,
    paddingLeft: open ? 12 : 0,
    paddingRight: open ? 16 : 0,
    paddingTop: 7,
    paddingBottom: 7,
    minHeight: 34,
    border: `1px solid transparent`,
    '& .MuiListItemIcon-root': {
      color: theme.palette.sidebar.contrastTextMuted,
      marginRight: theme.spacing(open ? 1 : 0),
    },
    '& .MuiListItemText-root': {
      margin: 0,
    },
    '&:not(.Mui-selected):hover': {
      backgroundColor: theme.palette.sidebar.highlight,
    },
  },
  '& .MuiListItemButton-root.Mui-selected, .MuiListItemButton-root.Mui-selected:hover':
    {
      backgroundColor: theme.palette.background.paper5,
      borderColor: theme.palette.dividerVariant,
      color: theme.palette.text.primary,
      '& .MuiListItemIcon-root': {
        color: theme.palette.sidebar.subtleContrastText,
      },
    },
}));

const getLogo = (brand, isOpen) => {
  switch (brand) {
    default:
      return isOpen ? '/assets/svg/logo.svg' : '/assets/svg/logo-notitle.svg';
  }
};

const MainMenuItem = ({
  showDrawer,
  Icon,
  label,
  children,
  to,
  exact,
  permission,
  permissions,
  feature,
  features,
  secondary,
  isSubmenu,
  basePath,
  openMenu,
  setOpenMenu,
  ...props
}) => {
  const location = useLocation();
  const isOpen = openMenu === label;
  const popupState = usePopupState({
    variant: 'menu',
    popupId: 'submenu',
  });
  const isChildActive = useMemo(() => {
    if (basePath) {
      return basePath.some((path) => location.pathname.startsWith(path));
    }
    return false;
  }, [location]);

  useEffect(() => {
    if (isChildActive) {
      setOpenMenu(label);
    }
  }, []);

  useEffect(() => {
    popupState.close();
  }, [location]);

  const toggle = () => {
    if (isOpen) {
      setOpenMenu();
    } else {
      setOpenMenu(label);
    }
  };

  const childMenu = children ? (
    showDrawer ? (
      <Collapse
        in={isOpen}
        timeout="auto"
        unmountOnExit
        sx={{ ml: 3, borderLeft: 1, pl: 3, borderColor: 'sidebar.border' }}
      >
        {React.createElement(children, {
          sx: {
            '& .MuiListItemButton-root': {
              color: 'sidebar.contrastTextMuted',
              mb: 0.5,
            },
          },
        })}
      </Collapse>
    ) : (
      <Menu
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        {...bindMenu(popupState)}
      >
        {React.createElement(children, {
          sx: {
            '& .MuiListItemButton-root': {
              color: 'text.primary',
            },
          },
        })}
      </Menu>
    )
  ) : null;

  const itemProps = childMenu && !showDrawer ? bindTrigger(popupState) : {};

  const element = (
    <SomeFeatures features={feature ? [feature] : features}>
      <Tooltip title={showDrawer ? '' : label} placement="right">
        <ListItem
          to={to}
          className={isChildActive ? 'Mui-selected' : ''}
          component={
            to
              ? forwardRef((props, ref) => (
                  <NavLink
                    {...props}
                    innerRef={ref}
                    activeClassName="Mui-selected"
                    exact={exact}
                  />
                ))
              : 'div'
          }
          exact={exact}
          onClick={children && toggle}
          {...itemProps}
          {...props}
          sx={{
            minHeight: 40,
            mb: 1,
            justifyContent: showDrawer ? 'initial' : 'center',
            color: 'inherit',
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 20,
              mr: showDrawer || isSubmenu ? 3 : 0,
              justifyContent: 'center',
              color: 'inherit',
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            slotProps={{
              primary: {
                color: 'inherit',
                fontWeight: 500,
              },
            }}
            primary={label}
            sx={
              !isSubmenu
                ? {
                    opacity: showDrawer ? 1 : 0,
                    width: showDrawer ? 'auto' : 0,
                    maxWidth: showDrawer ? 'auto' : 0,
                  }
                : {}
            }
          />
          {children && showDrawer && (
            <>{isOpen ? <ExpandLess /> : <ExpandMore />}</>
          )}
          {(showDrawer || isSubmenu) && secondary}
        </ListItem>
      </Tooltip>
      {childMenu}
    </SomeFeatures>
  );

  return element;
};

const Navigation = ({}) => {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState();
  const [t] = useTranslation('navigation');
  useEffect(() => {
    settingsPopoverState.close();
  }, [location]);

  const settingsPopoverState = usePopupState({
    variant: 'popover',
    popupId: 'settings',
  });

  const helpMenuState = usePopupState({
    variant: 'popover',
    popupId: 'help',
  });
  const config = useContext(ConfigContext);
  const account = useSelector((state) => state.settings.account);
  const user = useSelector((s) => s.user.user);
  const showDrawer = useSelector((state) => state.modals.showMobileNav);
  const hasImpersonate =
    account.account_id && account.account_id !== user.real_account_id;
  const issAdmin = account.account_type === 'admin';

  const onRestore = () => {
    sessionReset().then(() => window.location.replace('/admin'));
  };

  const openIntercom = () => {
    helpMenuState.close();
    if (window.Intercom) {
      window.Intercom('show');
    }
  };

  const MENU_OPTIONS = [
    {
      label: t('dashboard-link', 'Dashboard'),
      Icon: DashboardIcon,
      to: '/',
      exact: true,
    },
    {
      label: t('messages-link', 'Messages'),
      Icon: MessageIconWithUnread,
      to: '/messages',
      features: [FEATURES.SMS, FEATURES.EMAIL],
    },
    {
      label: t('people-link', 'People'),
      Icon: UserIcon,
      to: '/people',
      permission: MODULES.CONTACTS,
    },
    {
      label: t('groups-link', 'Groups'),
      Icon: UsersIcon,
      feature: FEATURES.GROUP,
      permission: MODULES.GROUPS,
      to: '/groups',
    },
    {
      label: t('connect-link', 'Connect'),
      Icon: ConnectIcon,
      basePath: ['/connect', '/pages', '/workflows'],
      features: [FEATURES.CONNECT_CARD, FEATURES.VIDEO, FEATURES.WORKFLOWS],
      permissions: [
        MODULES.CONNECT_CARDS,
        MODULES.VIDEO_NOTES,
        MODULES.WORKFLOWS,
      ],
      children: (props) => (
        <Box>
          <List disablePadding {...props}>
            <WithFeature feature={FEATURES.CONNECT_CARD} message={false}>
              <NavItem label={t('cards-link', 'Cards')} to="/connect/cards" />
            </WithFeature>
            <WithFeature feature={FEATURES.CONNECT_CARD} message={false}>
              <NavItem
                label={t('widgets-link', 'Widgets')}
                to="/connect/widgets"
              />
            </WithFeature>
            <WithFeature feature={FEATURES.WORKFLOWS} message={false}>
              <NavItem
                label={t('workflows-link', 'Workflows')}
                to="/workflows"
              />
            </WithFeature>
            <WithFeature feature={FEATURES.VIDEO} message={false}>
              <NavItem label={t('pages-link', 'Pages')} to="/pages" />
            </WithFeature>
            <WithFeature feature={FEATURES.DATACAPTURE} message={false}>
              <NavItem
                label={t('datacapture-link', 'Data Capture')}
                to="/datacapture"
              />
            </WithFeature>
          </List>
        </Box>
      ),
    },
    {
      label: t('tasks-link', 'Tasks'),
      Icon: CheckCircleIcon,
      to: '/tasks',
    },
    {
      label: t('talk-link', 'Talk'),
      Icon: PhoneIcon,
      to: '/calling',
      // features: [
      //   FEATURES.TALK_DIGITAL_RECEPTION,
      //   FEATURES.TALK_TEAM,
      //   FEATURES.TALK_OPERATOR,
      //   FEATURES.TALK_VOICEMAIL,
      // ],
    },
    {
      label: t('tools-link', 'Tools'),
      Icon: ToolIcon,
      basePath: [
        '/url-shortener',
        '/import-contacts',
        '/export-contacts',
        '/qr-code',
      ],
      children: (props) => (
        <Box>
          <List disablePadding {...props}>
            <NavItem label={t('qr-builder', 'QR Code Builder')} to="/qr-code" />
            <NavItem
              label={t('url-shortner', 'URL Shortener')}
              to="/url-shortener"
            />
            <NavItem
              label={t('import-link', 'Import People')}
              to="/import-contacts"
            />
            <NavItem
              label={t('export-link', 'Export People')}
              to="/export-contacts"
            />
            <NavItem label={t('files-link', 'File Storage')} to="/files" />
          </List>
        </Box>
      ),
    },
    {
      label: t('marketplace-link', 'Marketplace'),
      Icon: MarketIcon,
      to: '/marketplace',
    },
  ];

  return (
    <Drawer
      variant="permanent"
      open={showDrawer}
      sx={{
        '& .MuiDrawer-paper': {
          backgroundColor: (theme) => theme.palette.sidebar.main,
          color: (theme) => theme.palette.sidebar.contrastText,
          border: 0,
          pl: 2,
        },
      }}
    >
      <DrawerHeader open={showDrawer}>
        <img
          className={cx('logo', config.BRAND)}
          src={getLogo(config.BRAND, showDrawer)}
          alt="logo"
        />
      </DrawerHeader>
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
        <List disablePadding>
          {MENU_OPTIONS.map((option, idx) => {
            return (
              <MainMenuItem
                key={idx}
                {...option}
                showDrawer={showDrawer}
                openMenu={openMenu}
                setOpenMenu={setOpenMenu}
              />
            );
          })}
          {issAdmin && !hasImpersonate && (
            <MainMenuItem
              showDrawer={showDrawer}
              label={t('admin-link', 'Admin Panel')}
              Icon={UserCogIcon}
              to="/admin"
            />
          )}
          {hasImpersonate && (
            <MainMenuItem
              showDrawer={showDrawer}
              onClick={onRestore}
              label={t('admin-restore-link', 'Admin Panel')}
              Icon={LogoutIcon}
            />
          )}
        </List>
      </Box>
      <DrawerHeader
        sx={{
          mb: 2,
          mt: 0,
          pt: 0,
        }}
      >
        <List disablePadding sx={{ flex: 1 }}>
          <MainMenuItem
            showDrawer={showDrawer}
            to="/settings"
            label={t('settings-link', 'Settings')}
            Icon={SettingsIcon}
          />
          <MainMenuItem
            {...bindTrigger(helpMenuState)}
            showDrawer={showDrawer}
            label={t('resources-link', 'Resources')}
            Icon={CategoryIcon}
            secondary={<ChevronRightIcon />}
          />
          <Popover
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            {...bindPopover(helpMenuState)}
            sx={{
              color: 'text.primary',
            }}
          >
            <Box p={2} width={300}>
              <Typography sx={{ mb: 3 }} variant="h4">
                {t('resources-header', 'Resources')}
              </Typography>
              <Box>
                {config.BRAND === 'TIC' && (
                  <MainMenuItem
                    isSubmenu
                    showDrawer={showDrawer}
                    label={t('help-center-link', 'Help Center')}
                    Icon={() => <HelpIcon color="primary" />}
                    component="a"
                    target="_blank"
                    href="https://help.textinchurch.com"
                    secondary={<ArrowForwardOutlinedIcon color="primary" />}
                  />
                )}
                <MainMenuItem
                  isSubmenu
                  showDrawer={showDrawer}
                  label={t('academy-link', 'TIC Academy')}
                  Icon={() => <AcademyIcon color="primary" />}
                  to="/academy"
                  onClick={helpMenuState.close}
                  secondary={<ArrowForwardOutlinedIcon color="primary" />}
                />
                <MainMenuItem
                  isSubmenu
                  showDrawer={showDrawer}
                  label={t('message-us-link', 'Message Us')}
                  onClick={openIntercom}
                  Icon={() => <MessageDotsIcon color="primary" />}
                />
                <MainMenuItem
                  isSubmenu
                  data-canny-link
                  showDrawer={showDrawer}
                  label={t('feedback-link', 'Give feedback')}
                  Icon={() => <MessageReportIcon color="primary" />}
                  component="a"
                  target="_blank"
                  href="https://text-in-church.canny.io/feature-requests"
                />
                {config.BRAND === 'TIC' && (
                  <MainMenuItem
                    isSubmenu
                    showDrawer={showDrawer}
                    Icon={() => <LiveHelpIcon color="primary" />}
                    label={t('live-helpdesk-link', 'Live Help Desk')}
                    onClick={helpMenuState.close}
                    href="https://go.textinchurch.com/lunch_learn"
                    target="_blank"
                    secondary={<ArrowForwardOutlinedIcon color="primary" />}
                    component="a"
                  />
                )}
              </Box>
            </Box>
          </Popover>
        </List>
      </DrawerHeader>
    </Drawer>
  );
};

export default Navigation;
